import axios from "../../../../login/Axios-interceptor";
import { BASE_URL } from "./config";

/**
 * Document OCR pipeline — mirrors RegisterPatientForIpd.js:
 *
 * 1. POST patient-details-update/  (multipart)
 *    Body: request_id (UUID), file (image/pdf)
 *    Headers: Authorization only (browser sets multipart boundary)
 *    Response: { status: "Success", request_id?, message? }
 *
 * 2. POST get-patient-document-status/  (JSON)
 *    Body: { request_id: string }
 *    Headers: Authorization + Content-Type: application/json
 *    Response: { processing_status, document_json?, message? }
 */

function accessToken() {
  return sessionStorage.getItem("accessToken") || "";
}

function jsonAuthHeaders() {
  return {
    Authorization: `Bearer ${accessToken()}`,
    "Content-Type": "application/json",
  };
}

function multipartAuthHeaders() {
  return {
    Authorization: `Bearer ${accessToken()}`,
  };
}

export async function fetchPatientDocumentStatus(requestId) {
  const res = await axios.post(
    `${BASE_URL}get-patient-document-status/`,
    { request_id: requestId },
    { headers: jsonAuthHeaders() }
  );
  return res?.data || {};
}

export async function pollPatientDocumentStatus(
  requestId,
  { intervalMs = 10000, maxAttempts = 6 } = {}
) {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const data = await fetchPatientDocumentStatus(requestId);
    const procStatus = String(data.processing_status || "").toLowerCase();
    if (procStatus === "completed") return data.document_json || null;
    if (procStatus === "failed" || procStatus === "error") {
      throw new Error(data.message || "Document processing failed.");
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  return null;
}

export async function submitPatientDocument(file, requestId) {
  const formData = new FormData();
  formData.append("request_id", requestId);
  formData.append("file", file);

  const res = await axios.post(
    `${BASE_URL}patient-details-update/`,
    formData,
    { headers: multipartAuthHeaders() }
  );

  return res?.data || {};
}

/**
 * Server issues the request_id and a signed, short-lived upload token for the
 * cross-device QR. Throws on failure — callers must not fall back to putting
 * session credentials in the QR.
 */
export async function createQrUploadSession(kind = "doc") {
  const res = await axios.post(
    `${BASE_URL}ipd-qr-session/`,
    { kind },
    { headers: jsonAuthHeaders() }
  );
  const data = res?.data || {};
  if (data.status !== "Success" || !data.request_id || !data.token) {
    throw new Error(data.message || "Could not create QR upload session.");
  }
  return { requestId: data.request_id, token: data.token };
}

/** Fetch the photo uploaded from the phone for a photo QR session, as a data URL. */
export async function fetchQrPhoto(token) {
  const res = await axios.post(
    `${BASE_URL}ipd-qr-photo/`,
    { token },
    { headers: jsonAuthHeaders() }
  );
  const data = res?.data || {};
  if (data.status !== "Success") {
    throw new Error(data.message || "Could not fetch the photo.");
  }
  if (!data.found || !data.image_base64) return { found: false, dataUrl: null };
  return {
    found: true,
    dataUrl: `data:${data.content_type || "image/jpeg"};base64,${data.image_base64}`,
  };
}

export async function fetchPatientListWithAbhaInfo() {
  const res = await axios.get(
    `${BASE_URL}get_patient_list_with_aabha_info/`,
    { headers: jsonAuthHeaders() }
  );
  const data = res?.data;
  return data?.data || (Array.isArray(data) ? data : []);
}
